import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, Calendar } from "lucide-react";
import DOMPurify from "dompurify";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

/** Minimal safe Markdown-ish renderer — supports headings, bold, italic, inline code, links, lists, paragraphs.
 *  HTML is escaped before substitutions to prevent injection. */
function renderMarkdown(md) {
  if (!md) return "";
  const esc = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const lines = esc.split(/\r?\n/);
  const out = [];
  let inList = false;
  let inPara = [];

  const flushPara = () => {
    if (inPara.length) {
      let para = inPara.join(" ");
      para = para
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/`([^`]+)`/g, '<code class="bg-cream-surface rounded px-1 text-sm">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g,
          '<a href="$2" class="text-brand hover:underline" target="_blank" rel="noreferrer">$1</a>');
      out.push(`<p class="font-manrope text-ink leading-relaxed my-4">${para}</p>`);
      inPara = [];
    }
  };

  const flushList = () => {
    if (inList) { out.push("</ul>"); inList = false; }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); flushList(); continue; }
    const h1 = /^#\s+(.*)$/.exec(line);
    const h2 = /^##\s+(.*)$/.exec(line);
    const h3 = /^###\s+(.*)$/.exec(line);
    const li = /^[-*]\s+(.*)$/.exec(line);

    if (h1) { flushPara(); flushList(); out.push(`<h1 class="font-outfit text-3xl lg:text-4xl font-medium text-ink mt-10 mb-4">${h1[1]}</h1>`); continue; }
    if (h2) { flushPara(); flushList(); out.push(`<h2 class="font-outfit text-2xl lg:text-3xl font-medium text-ink mt-8 mb-3">${h2[1]}</h2>`); continue; }
    if (h3) { flushPara(); flushList(); out.push(`<h3 class="font-outfit text-xl font-medium text-ink mt-6 mb-2">${h3[1]}</h3>`); continue; }
    if (li) {
      flushPara();
      if (!inList) { out.push('<ul class="list-disc pl-6 space-y-2 my-4 font-manrope text-ink">'); inList = true; }
      let item = li[1]
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/`([^`]+)`/g, '<code class="bg-cream-surface rounded px-1 text-sm">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g,
          '<a href="$2" class="text-brand hover:underline" target="_blank" rel="noreferrer">$1</a>');
      out.push(`<li>${item}</li>`);
      continue;
    }
    flushList();
    inPara.push(line);
  }
  flushPara();
  flushList();
  return out.join("\n");
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data);
        if (data.seo_description) {
          const m = document.querySelector('meta[name="description"]');
          if (m) m.setAttribute("content", data.seo_description);
        }
        if (data.title) document.title = `${data.title} · Hysteregistry`;
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Post not found.");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) {
    return <div className="container-x py-16"><p className="font-manrope text-ink-soft animate-soft-pulse">Loading…</p></div>;
  }

  if (error || !post) {
    return (
      <div className="container-x py-16" data-testid="blog-post-error">
        <div className="card-soft p-10 text-center max-w-xl mx-auto">
          <h1 className="font-outfit text-2xl text-ink">This post couldn't be found.</h1>
          <p className="font-manrope text-ink-soft mt-3">{error}</p>
          <Link to="/blog" className="btn-primary mt-6 inline-block">Back to all posts</Link>
        </div>
      </div>
    );
  }

  return (
    <article className="container-x py-12 lg:py-16 max-w-3xl" data-testid="blog-post-page">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand mb-8" data-testid="blog-post-back">
        <ArrowLeft className="h-4 w-4" /> All posts
      </Link>

      <div className="flex items-center gap-2 flex-wrap">
        {(post.tags || []).map((t) => (
          <Link to={`/blog?tag=${encodeURIComponent(t)}`} key={t} className="tag-pill hover:bg-brand hover:text-white transition-colors">
            #{t}
          </Link>
        ))}
      </div>

      <h1 className="mt-4 font-outfit text-4xl lg:text-5xl font-medium text-ink tracking-tight leading-tight" data-testid="blog-post-title">
        {post.title}
      </h1>

      <div className="mt-4 flex items-center gap-3 font-manrope text-sm text-ink-soft">
        <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> {formatDate(post.published_at)}</span>
        {post.author_name && <span>· by {post.author_name}</span>}
      </div>

      {post.cover_image_url && (
        <div className="mt-8 rounded-3xl overflow-hidden card-soft">
          <img src={post.cover_image_url} alt={post.title} className="w-full aspect-[2/1] object-cover" />
        </div>
      )}

      <div
        className="mt-10"
        data-testid="blog-post-body"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(renderMarkdown(post.body), {
            ALLOWED_TAGS: ["h1", "h2", "h3", "p", "ul", "ol", "li", "strong", "em", "code", "a", "br"],
            ALLOWED_ATTR: ["href", "class", "target", "rel"],
            ALLOW_DATA_ATTR: false,
          }),
        }}
      />

      <div className="mt-16 card-soft p-8 text-center">
        <p className="font-outfit text-xl text-ink">Starting your own Hysteregistry?</p>
        <p className="font-manrope text-sm text-ink-soft mt-2 mx-auto max-w-md">
          Free to use, shareable, and private where it counts.
        </p>
        <Link to="/register" className="btn-primary mt-5 inline-block" data-testid="blog-post-cta">
          Start yours
        </Link>
      </div>
    </article>
  );
}
